import React from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import StudentUserDetailAPIs from 'utilities/api/StudentUserDetailAPIs';
import { updateUser } from '../../redux/slices/userSlice';
import ImageBaseURL from 'utilities/api/axios';

function ProfileImage() {
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [editMode, setEditMode] = React.useState(false);
  const [editedUser, setEditedUser] = React.useState(user);
  const [imageFile, setImageFile] = React.useState(null);

  const handleEdit = () => {
    setEditedUser({
      ...user,
      profile_pic: getProfilePicUrl(user.profile_pic), // Ensure profile_pic is correctly set
    });
    setEditMode(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
  
    // Check for spaces in the username field
    if (name === 'username' && /\s/.test(value)) {
      alert("Usernames cannot contain spaces.");
      return; // Stop further execution if a space is detected
    }
  
    setEditedUser({
      ...editedUser,
      [name]: value,
    });
  };  

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setEditedUser({
        ...editedUser,
        profile_pic: URL.createObjectURL(file),
      });
    }
  };

  const handleSave = async () => {
    // Check if the username contains spaces
    if (/\s/.test(editedUser.username)) {
      alert("Usernames cannot contain spaces.");
      return; // Prevent saving if validation fails
    }
  
    try {
      const formData = new FormData();
      formData.append('username', editedUser.username);
      if (imageFile) {
        formData.append('profile_pic', imageFile);
      }
  
      const updatedUser = await StudentUserDetailAPIs.StudentDetailUpdate(user.id, formData);
      console.log('Updated user:', updatedUser);
      dispatch(updateUser(updatedUser));
      setEditMode(false);
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to save changes. Please try again.');
    }
  };  

  const getProfilePicUrl = (profilePicPath) => {
    return profilePicPath ? `${ImageBaseURL.defaults.baseURL}${profilePicPath}` : '';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        margin: { xs: '0.5rem ', sm: '1rem ' }, 
        width: '95%', 
        border: '1px solid #1976d2',
        borderRadius: '10px',
        padding: { xs: '10px', sm: '15px', md: '25px' }, 
        position: 'relative',
        boxSizing: 'border-box', 
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row', 
          flexGrow: 1,
          width: '100%',
        }}
      >
        <Avatar
          alt="Profile"
          src={editMode && editedUser.profile_pic ? editedUser.profile_pic : getProfilePicUrl(user.profile_pic)}
          sx={{
            width: { xs: 80, sm: 120, md: 150, lg: 200 }, 
            height: { xs: 80, sm: 120, md: 150, lg: 200 },
            border: '2px solid rgb(231,164,67)',
          }}
        />
        {editMode && (
          <>
            <label htmlFor="profile-image-upload">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
                id="profile-image-upload"
              />
              <Button
                variant="contained"
                component="span"
                sx={{
                  marginLeft: { xs: '0.5rem', sm: '1rem' }, 
                  fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' }, 
                  padding: { xs: '4px 8px', sm: '6px 12px' }, 
                }}
              >
                Upload New Image
              </Button>
            </label>
          </>
        )}
        <Box
          sx={{
            marginLeft: { xs: '1rem', sm: '2rem', md: '3rem' },
          }}
        >
          {editMode ? (
            <TextField
              label="Username"
              name="username"
              value={editedUser.username}
              onChange={handleChange}
              fullWidth
              sx={{
                marginBottom: '0.5rem',
                '& .MuiInputBase-input': {
                  fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' }, 
                },
              }}
            />
          ) : (
            <Typography
              variant="h4"
              sx={{
                color: 'rgb(39,63,119)',
                fontWeight: '500',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.2rem', lg: '2.5rem' }, 
                marginBottom: '0.5rem',
              }}
            >
              {user.username}
            </Typography>
          )}
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' }, 
            }}
          >
            {user.staff ? 'Staff' : 'Student'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' }, 
            }}
          >
            {user.position}
          </Typography>
        </Box>
      </Box>
      {editMode ? (
        <Box
          sx={{
            position: 'absolute',
            top: { xs: '5px', sm: '8px', md: '12px' }, 
            right: { xs: '5px', sm: '8px', md: '12px' },
            cursor: 'pointer',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{
              display: 'flex',
              alignItems: 'center',
              padding: { xs: '3px 6px', sm: '4px 8px', md: '6px 12px' }, 
              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' }, 
            }}
          >
            Save
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            position: 'absolute',

            right: { xs: '5px', sm: '8px', md: '12px' },
            cursor: 'pointer',
          }}
          onClick={handleEdit}
        >
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
               padding: { xs: '2px 8px', sm: '4px 10px' }, 
              color: 'rgb(122,122,122)',
              fontSize: '1.1rem',
              border: '1px solid rgb(122,122,122)',
              borderRadius: '20px',
            }}
          >
            Edit
            <EditIcon
              sx={{
                fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' }, 
                color: 'rgb(122,122,122)',
                marginLeft: '5px'
              }}
            />
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default ProfileImage;